import { useMemo } from 'react';

import {
  PinMapEditor,
  type PinMapItem,
  type PinMapLegendEntry,
} from '@/components/shared/map/PinMapEditor';
import { fallbackPinColor, PIN_UNASSIGNED_COLOR } from '@/components/shared/map/pinColors';
import { buildTemplateMapItems } from '@/components/templates/templateMapItems';
import { useUpdateTemplatePin } from '@/components/templates/useUpdateTemplatePin';
import type { TemplateChallengeGroup, TemplateChallengeLink } from '@/config/objects/templates';

export interface TemplateMapEditorProps {
  templateId: string;
  /** Groups (for pin colors + legend). */
  groups: TemplateChallengeGroup[];
  /** Flat list of every template challenge link (grouped + ungrouped). */
  challenges: TemplateChallengeLink[];
  googleMapsApiKey?: string;
  editable: boolean;
}

const UNGROUPED_KEY = '__ungrouped__';

/**
 * All-pins map for an event template's challenges. Each pin shows the resolved
 * location — the per-template override if set, else the linked library
 * challenge's location. Dragging a pin sets the template override (never the
 * library challenge). See docs/CHALLENGE_LOCATION_HIERARCHY.md.
 */
export function TemplateMapEditor({
  templateId,
  groups,
  challenges,
  googleMapsApiKey,
  editable,
}: TemplateMapEditorProps) {
  const updatePin = useUpdateTemplatePin(templateId);

  const items = useMemo<PinMapItem[]>(
    () => buildTemplateMapItems(groups, challenges),
    [groups, challenges],
  );

  const legend = useMemo<PinMapLegendEntry[]>(() => {
    const present = new Set(challenges.map((c) => c.groupId ?? UNGROUPED_KEY));
    const entries: PinMapLegendEntry[] = groups
      .filter((group) => present.has(group.id))
      .map((group, index) => ({ label: group.name, color: group.color || fallbackPinColor(index) }));
    if (present.has(UNGROUPED_KEY)) {
      entries.push({ label: 'Ungrouped', color: PIN_UNASSIGNED_COLOR });
    }
    return entries;
  }, [groups, challenges]);

  return (
    <PinMapEditor
      items={items}
      editable={editable}
      legend={legend}
      fillViewport
      googleMapsApiKey={googleMapsApiKey}
      onMove={(item, latitude, longitude) =>
        updatePin.mutate({ linkId: item.id, latitude, longitude })
      }
    />
  );
}
